import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getPartnerContractsList } from '../services'
import {
    createPartnerContractColumns,
    createPartnerContractFilters,
    PARTNER_CONTRACT_FILTER_DEFAULTS,
    type PartnerContractFilters,
} from '../utils/partner-contracts-config'

/**
 * Business logic hook for Partner Contracts List
 * Manages data fetching, pagination, sorting, and filtering
 */
export const usePartnerContracts = () => {
    const { t } = useTranslation('contract')

    // URL params management
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<PartnerContractFilters>({
        defaults: { sort_by: 'contract_id', sort_order: 'desc', page_size: 20 },
        filterDefaults: PARTNER_CONTRACT_FILTER_DEFAULTS,
    })

    // Form for filter controls
    const { control } = useForm<PartnerContractFilters>({
        defaultValues: filters,
    })

    // Fetch partner contracts data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_PARTNERS, queryParams],
        queryFn: () => getPartnerContractsList(queryParams),
        placeholderData: true,
    })

    // Memoized columns & filters
    const columns = useMemo(() => createPartnerContractColumns(t), [t])
    const filterItems = useMemo(() => createPartnerContractFilters(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        contracts: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}

export default usePartnerContracts
